import React, { useState, useEffect, useCallback } from 'react';
import {
  
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  
} from '@mui/material';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,Area, Legend,
  ReferenceLine  } from 'recharts';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';

const DIFC_COLORS = {
  primary: '#003366',
  secondary: '#00796B',
  accent: '#FFC72C',
  background: '#F5F8FA',
  textPrimary: '#1E2D3B',
  textSecondary: '#4A6B82',
};

function ParkingPrediction({ selectedArea }) {
  
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [historicalLoading, setHistoricalLoading] = useState(false);

  // Helper function to get max capacity based on selected area
  const getMaxCapacity = useCallback(() => {
    return selectedArea === 'gate_village' ? 1600 : 2009;
  }, [selectedArea]);

  // Helper function to get Y-axis domain based on selected area
  const getYAxisDomain = () => {
    const maxCapacity = getMaxCapacity();
    const minValue = selectedArea === 'gate_village' ? 800 : 1300; // Lower bound for Gate Village
    return [minValue, maxCapacity];
  };
  const [predictions, setPredictions] = useState(() => {
    const savedPredictions = localStorage.getItem('parkingPredictions');
    return savedPredictions ? JSON.parse(savedPredictions) : [];
  });
  const [showOptimizeButton, setShowOptimizeButton] = useState(() => {
    return localStorage.getItem('showOptimizeButton') === 'true';
  });
  const [historicalData, setHistoricalData] = useState([]);
  // const [availableYears, setAvailableYears] = useState([]); // Unused for now

  // Get current date and format it
  const getCurrentMonth = () => {
    const date = new Date();
    return date.toLocaleString('default', { month: 'long' }) + ' ' + date.getFullYear();
  };

  // const getCurrentYear = () => {
  //   return new Date().getFullYear();
  // }; // Unused for now

  // const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth()); // Unused for now

  const fetchHistoricalData = useCallback(async () => {
    setHistoricalLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/historical?area=${selectedArea}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      
      if (data.status === 'success' && data.historical) {
        // Get current date info
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth(); // 0-based (0 = Jan, 11 = Dec)
        const currentYear = currentDate.getFullYear();
        
        // Generate array of last 12 months (excluding current month)
        const last12Months = [];
        for (let i = 1; i <= 12; i++) {
          const date = new Date(currentYear, currentMonth - i, 1);
          const monthName = date.toLocaleString('default', { month: 'short' }); // Get 3-letter month
          const year = date.getFullYear();
          last12Months.unshift({ // unshift to maintain chronological order
            monthKey: `${monthName} ${year}`,
            monthLabel: monthName,
            year: year,
            monthIndex: date.getMonth()
          });
        }
        
        // Create a map for quick lookup of historical data
        const historicalMap = {};
        data.historical.forEach(item => {
          historicalMap[item.monthLabel] = item;
        });
        
        // Filter and map the data to only include the last 12 months
        const filteredData = last12Months.map(monthInfo => {
          const historicalEntry = historicalMap[monthInfo.monthKey];
          const yearShort = monthInfo.year.toString().slice(-2); // Get last 2 digits of year
          return {
            month: `${monthInfo.monthLabel}${yearShort}`, // Format as "May25"
            monthLabel: monthInfo.monthKey, // Keep full label for tooltip
            max_occupancy: historicalEntry ? historicalEntry.max_occupancy : null,
            maxCapacity: getMaxCapacity(),
            year: monthInfo.year
          };
        });

        setHistoricalData(filteredData);
        
        // Set available years from the filtered data
        // const yearsInData = [...new Set(filteredData.map(item => item.year))];
        // setAvailableYears(yearsInData); // Unused for now
        
      } else {
        setHistoricalData([]);
      }
    } catch (error) {
      setHistoricalData([]);
      console.error('Historical fetch error:', error);
    } finally {
      setHistoricalLoading(false);
    }
  }, [selectedArea, getMaxCapacity]);

  // Forecast generator (placed before effects that depend on it)
  const handleGenerateForecast = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/predictions?area=${selectedArea}`, { 
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received data:', data); // Debug log
      if (data.status === 'success' && data.predictions) {
        // Generate next 12 months starting from current month
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth(); // 0-based (0 = Jan, 11 = Dec)
        const currentYear = currentDate.getFullYear();
        
        // Generate array of next 12 months starting from current month
        const next12Months = [];
        for (let i = 0; i < 12; i++) {
          const date = new Date(currentYear, currentMonth + i, 1);
          const monthName = date.toLocaleString('default', { month: 'short' }); // Get 3-letter month
          const year = date.getFullYear();
          const yearShort = year.toString().slice(-2); // Get last 2 digits of year
          next12Months.push({
            monthKey: monthName, // Just the month name for backend lookup
            monthLabel: `${monthName}${yearShort}`, // Display format like "Jun25"
            year: year,
            monthIndex: date.getMonth()
          });
        }

        // Transform backend predictions to chart data using generated months
        const transformedPredictions = next12Months.map((monthInfo, index) => {
          // Try to find matching prediction from backend
          const backendPredictions = Object.entries(data.predictions);
          let predictionValue = 1500; // default value
          
          // Look for matching month in backend data
          const matchingPrediction = backendPredictions.find(([_, obj]) => {
            // Check various formats the backend might use
            const backendMonth = obj.month;
            return (
              backendMonth === monthInfo.monthKey || // "Jun"
              backendMonth === monthInfo.monthLabel || // "Jun25"
              backendMonth === `${monthInfo.monthKey} ${monthInfo.year}` || // "Jun 2025"
              backendMonth.toLowerCase() === monthInfo.monthKey.toLowerCase()
            );
          });
          
          if (matchingPrediction) {
            predictionValue = Math.round(matchingPrediction[1].value);
          } else if (backendPredictions[index]) {
            // Fallback: use prediction by index if no month match
            predictionValue = Math.round(backendPredictions[index][1].value);
          }
          
          return {
            monthLabel: monthInfo.monthLabel, // Format: "Jun25", "Jul25", etc.
            max_occupancy: predictionValue,
            maxCapacity: getMaxCapacity()
          };
        });

        setPredictions(transformedPredictions);
        setShowOptimizeButton(true);
      } else {
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('Forecast error:', error);
      setPredictions([]);
      setShowOptimizeButton(false);
    } finally {
      setLoading(false);
    }
  }, [selectedArea, getMaxCapacity]);

  // Add effect to save predictions when they change
  useEffect(() => {
    if (predictions.length > 0) {
      localStorage.setItem('parkingPredictions', JSON.stringify(predictions));
      localStorage.setItem('showOptimizeButton', 'true');
    }
  }, [predictions]);


  // Load historical data on component mount
  useEffect(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData]);

  // Reload data when area changes
  useEffect(() => {
    fetchHistoricalData();
    // Clear predictions when area changes
    setPredictions([]);
    // Always show optimize button when switching areas
    setShowOptimizeButton(true);
  }, [selectedArea, fetchHistoricalData]);

  // Auto-generate forecast when component loads or area changes
  useEffect(() => {
    handleGenerateForecast();
  }, [selectedArea, handleGenerateForecast]);


  // Optional: Add cleanup function to clear data
  // const handleClearData = () => {
  //   localStorage.removeItem('parkingPredictions');
  //   localStorage.removeItem('showOptimizeButton');
  //   setPredictions([]);
  //   setShowOptimizeButton(false);
  // }; // Unused for now

  return (
    <Box sx={{ p: 4, backgroundColor: DIFC_COLORS.background, minHeight: '100vh' }}>
      {/* Prediction Chart */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 4, // Add margin bottom for spacing between charts
        }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontFamily: 'Lufga',
            fontWeight: 600,
            color: DIFC_COLORS.primary,
            mb: 2,
            width: '100%',
            textAlign: 'center',
            display: 'block',
          }}
        >
          {`${new Date().getFullYear()} Monthly Occupancy Forecast - DIFC ${selectedArea === 'gate_district' ? 'Gate District' : 'Gate Village'}`}
        </Typography>


        {/* Row for current month and buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            mb: 4,
            width: '100%',
          }}
        >
          <TextField
            label="Current Month"
            value={getCurrentMonth()}
            disabled
            sx={{
              width: '180px',
              '& .Mui-disabled': {
                WebkitTextFillColor: '#000000',
                color: '#000000'
              }
            }}
            InputProps={{
              style: { textAlign: 'center', fontFamily: 'Lufga' }
            }}
          />
          
          {/* Forecast Button */}
          <Button
            variant="contained"
            onClick={handleGenerateForecast}
            sx={{
              fontFamily: 'Lufga',
              fontWeight: 500,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px',
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              }
            }}
          >
            Generate Forecast
          </Button>

          {/* Start Optimizing Button (appears after forecast) */}
          {showOptimizeButton && (
            <Button
              variant="contained"
              onClick={() => navigate('/optimization')}
              sx={{
                fontFamily: 'Lufga',
                fontWeight: 500,
                backgroundColor: '#1f2937',
                color: 'white',
                padding: '12px',
                borderRadius: '4px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#374151',
                }
              }}
            >
              Start Optimizing
            </Button>
          )}
        </Box>

        {/* Prediction Chart */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, width: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={predictions}
                margin={{ top: 20, right: 40, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="monthLabel"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#6b7280", fontFamily: "Lufga" }}
                  angle={0}
                  textAnchor="middle"
                  height={80}
                  interval={0}
                  tickMargin={10}
                />
                <YAxis
                  domain={getYAxisDomain()}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#6b7280", fontFamily: "Lufga" }}
                  tickFormatter={(value) => Math.round(value)}
                />
                <Tooltip
                  formatter={(value, name) => [Math.round(value), name]}
                  contentStyle={{
                    fontFamily: "Lufga",
                    backgroundColor: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                />
                <Legend wrapperStyle={{ fontFamily: "Lufga" }} />
                <ReferenceLine y={getMaxCapacity()} stroke="#374151" strokeDasharray="8 8" strokeWidth={2} />

                {/* Area under the prediction line */}
                <Area
                  type="monotone"
                  dataKey="max_occupancy"
                  stroke={false}
                  fill="#22c55e" // light green
                  fillOpacity={0.7}
                  name="Utilized Space"
                />
                {/* Area above the prediction line up to maxCapacity */}
                <Area
                  type="monotone"
                  dataKey="maxCapacity"
                  stroke={false}
                  fill='#4B79BE' // blue
                  fillOpacity={0.3}
                  name="Max Capacity"
                  baseLine={entry => entry.max_occupancy}
                />

                <Line
                  type="monotone"
                  dataKey="max_occupancy"
                  stroke="#1f2937"
                  strokeWidth={3}
                  dot={{ fill: "#1f2937", strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: "#1f2937", stroke: "#fff", strokeWidth: 2 }}
                  name="Predicted Occupancy"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      {/* Historical Chart - Always visible */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontFamily: 'Lufga',
            fontWeight: 600,
            color: DIFC_COLORS.primary,
            mb: 3,
            width: '100%',
            textAlign: 'center',
            display: 'block',
          }}
        >
          {`Historical Monthly Occupancy Data - DIFC ${selectedArea === 'gate_district' ? 'Gate District' : 'Gate Village'}`}
        </Typography>

        {/* Historical Chart */}
        {historicalLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, width: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={historicalData}
                margin={{ top: 20, right: 40, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#6b7280", fontFamily: "Lufga" }}
                  angle={0}
                  textAnchor="middle"
                  height={80}
                  interval={0}
                  tickMargin={10}
                />
                <YAxis
                  domain={getYAxisDomain()}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#6b7280", fontFamily: "Lufga" }}
                  tickFormatter={(value) => Math.round(value)}
                />
                <Tooltip
                  formatter={(value, name) => [Math.round(value), name]}
                  contentStyle={{
                    fontFamily: "Lufga",
                    backgroundColor: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                />
                <Legend wrapperStyle={{ fontFamily: "Lufga" }} />
                <ReferenceLine y={getMaxCapacity()} stroke="#374151" strokeDasharray="8 8" strokeWidth={2} />

                {/* Green area under the historical line */}
                <Area
                  type="monotone"
                  dataKey="max_occupancy"
                  stroke={false}
                  fill="#22c55e" // green
                  fillOpacity={0.7}
                  name="Historical Utilized Space"
                />
                {/* Blue area above the historical line up to maxCapacity */}
                <Area
                  type="monotone"
                  dataKey="maxCapacity"
                  stroke={false}
                  fill='#4B79BE' // blue (same as prediction chart)
                  fillOpacity={0.3}
                  name="Max Capacity"
                  baseLine={entry => entry.max_occupancy}
                />

                <Line
                  type="monotone"
                  dataKey="max_occupancy"
                  stroke="#1f2937"
                  strokeWidth={3}
                  dot={{ fill: "#1f2937", strokeWidth: 2, r: 5 }}
                  name="Historical Occupancy (Last 12 Months)"
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ParkingPrediction;